/**
 * Parking analytics — aggregated KPIs + simple SVG charts for the parking
 * module. Five sections (each = one Card):
 *
 *   A. Occupancy trend  — daily occupied vs total (line/bar)
 *   B. Disputes         — month-over-month opened/resolved + top reasons
 *   C. EV revenue       — month/year picker; kWh, ₹, sessions, by-charger
 *   D. Marketplace      — active listings, completed rentals, society cut
 *   E. Utilization      — by-zone heatmap-ish bars + underused slots list
 *
 * No external chart deps — pure SVG bar/line drawn inline. Date range filter
 * at the top defaults to last 90 days; EV revenue has its own month picker
 * because revenue is naturally a monthly report.
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  TrendingUp,
  MessageSquareWarning,
  Zap,
  Store,
  Activity
} from 'lucide-react';
import { get, getErrorMessage } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { formatDate } from '@/lib/format';

// ───── response shapes ─────

interface OccupancyResponse {
  series: Array<{ date: string; occupied: number; total: number }>;
  avg_occupancy_pct: number;
  peak: { date: string; occupied: number } | null;
  lowest: { date: string; occupied: number } | null;
}

interface DisputesResponse {
  series: Array<{ month: string; opened: number; resolved: number }>;
  total_opened: number;
  total_resolved: number;
  resolution_rate_pct: number;
  avg_resolution_hours: number;
  top_reasons: Array<{ reason: string; count: number }>;
}

interface EvRevenueResponse {
  period_year: number;
  period_month: number;
  total_kwh: number;
  total_amount_inr: number;
  session_count: number;
  by_charger: Array<{
    charger_id: string;
    label?: string | null;
    kwh: number;
    amount_inr: number;
    sessions: number;
  }>;
}

interface MarketplaceResponse {
  active_listings: number;
  completed_rentals: number;
  society_cut_total_inr: number;
  recent_transactions: Array<{
    id: string;
    slot_number: string;
    from_unit?: string | null;
    to_unit?: string | null;
    amount_inr: number;
    completed_at: string;
  }>;
}

interface UtilizationResponse {
  by_zone: Array<{ zone_id: string; zone_name: string; occupied_pct: number }>;
  top_underused: Array<{
    slot_id: string;
    slot_number: string;
    zone_name?: string | null;
    owner_unit?: string | null;
    days_idle: number;
  }>;
  top_disputed: Array<{
    slot_id: string;
    slot_number: string;
    zone_name?: string | null;
    dispute_count: number;
  }>;
}

interface UnderusedResponse {
  items: Array<{
    slot_id: string;
    slot_number: string;
    zone_name?: string | null;
    owner_unit?: string | null;
    entries_30d: number;
  }>;
}

// ───── helpers ─────

function defaultRange(days = 90): { from: string; to: string } {
  const now = new Date();
  const to = now.toISOString().slice(0, 10);
  const fromDate = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
  const from = fromDate.toISOString().slice(0, 10);
  return { from, to };
}

function formatInr(amount: number | null | undefined): string {
  if (amount === null || amount === undefined) return '—';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(amount);
}

// ───── page ─────

export function ParkingAnalyticsPage(): JSX.Element {
  const initial = defaultRange(90);
  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);

  const now = new Date();
  const [evYear, setEvYear] = useState(now.getFullYear());
  const [evMonth, setEvMonth] = useState(now.getMonth() + 1);

  return (
    <PageHeader
      title="Parking analytics"
      description="Occupancy, disputes, EV revenue, marketplace and slot utilisation."
    >
      {/* Top filter strip */}
      <Card className="mb-vt-4">
        <CardContent className="flex flex-wrap items-end gap-vt-3 p-vt-4">
          <div className="flex flex-col gap-vt-1">
            <Label htmlFor="an-from">From</Label>
            <Input
              id="an-from"
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="w-44"
            />
          </div>
          <div className="flex flex-col gap-vt-1">
            <Label htmlFor="an-to">To</Label>
            <Input id="an-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-44" />
          </div>
          <p className="text-vt-12 text-vt-text-muted">
            Window applies to Occupancy, Disputes, Marketplace and Utilisation. EV revenue uses its own
            month / year picker.
          </p>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-vt-4">
        <OccupancySection from={from} to={to} />
        <DisputesSection from={from} to={to} />
        <EvRevenueSection
          year={evYear}
          month={evMonth}
          onYearChange={setEvYear}
          onMonthChange={setEvMonth}
        />
        <MarketplaceSection from={from} to={to} />
        <UtilizationSection from={from} to={to} />
      </div>
    </PageHeader>
  );
}

// ─── A. Occupancy ─────────────────────────────────────────

function OccupancySection({ from, to }: { from: string; to: string }): JSX.Element {
  const query = useQuery({
    queryKey: ['parking', 'analytics', 'occupancy', { from, to }],
    queryFn: () => {
      const qs = new URLSearchParams({ from, to });
      return get<OccupancyResponse>(`/parking/analytics/occupancy?${qs.toString()}`);
    }
  });
  const series = query.data?.series ?? [];
  const values = series.map((s) => (s.total > 0 ? (s.occupied / s.total) * 100 : 0));
  const max = Math.max(100, ...values, 1);

  return (
    <Card>
      <CardContent className="space-y-vt-3 p-vt-4">
        <SectionHeader icon={<TrendingUp className="h-4 w-4" />} title="A. Occupancy trend" />
        {query.isLoading ? (
          <Skeleton className="h-40 w-full" />
        ) : query.isError ? (
          <p className="text-vt-alert">{getErrorMessage(query.error)}</p>
        ) : series.length === 0 ? (
          <p className="py-vt-4 text-center text-vt-13 text-vt-text-muted">
            No occupancy data in this window.
          </p>
        ) : (
          <>
            <KpiRow
              items={[
                {
                  label: 'Avg occupancy',
                  value: `${(query.data?.avg_occupancy_pct ?? 0).toFixed(1)}%`
                },
                {
                  label: 'Peak day',
                  value: query.data?.peak
                    ? `${query.data.peak.occupied} on ${formatDate(query.data.peak.date)}`
                    : '—'
                },
                {
                  label: 'Lowest day',
                  value: query.data?.lowest
                    ? `${query.data.lowest.occupied} on ${formatDate(query.data.lowest.date)}`
                    : '—'
                }
              ]}
            />
            <BarChart
              values={values}
              max={max}
              labels={series.map((s) => s.date)}
              metricLabel="% occupancy"
              tone="primary"
            />
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ─── B. Disputes ──────────────────────────────────────────

function DisputesSection({ from, to }: { from: string; to: string }): JSX.Element {
  const query = useQuery({
    queryKey: ['parking', 'analytics', 'disputes', { from, to }],
    queryFn: () => {
      const qs = new URLSearchParams({ from, to });
      return get<DisputesResponse>(`/parking/analytics/disputes?${qs.toString()}`);
    }
  });
  const series = query.data?.series ?? [];
  const opened = series.map((r) => r.opened);
  const resolved = series.map((r) => r.resolved);
  const max = Math.max(1, ...opened, ...resolved);

  return (
    <Card>
      <CardContent className="space-y-vt-3 p-vt-4">
        <SectionHeader icon={<MessageSquareWarning className="h-4 w-4" />} title="B. Disputes" />
        {query.isLoading ? (
          <Skeleton className="h-40 w-full" />
        ) : query.isError ? (
          <p className="text-vt-alert">{getErrorMessage(query.error)}</p>
        ) : series.length === 0 && !query.data ? (
          <p className="py-vt-4 text-center text-vt-13 text-vt-text-muted">No dispute data.</p>
        ) : (
          <>
            <KpiRow
              items={[
                { label: 'Total opened', value: String(query.data?.total_opened ?? 0) },
                {
                  label: 'Resolution rate',
                  value: `${(query.data?.resolution_rate_pct ?? 0).toFixed(0)}%`
                },
                {
                  label: 'Avg time-to-resolve',
                  value: `${(query.data?.avg_resolution_hours ?? 0).toFixed(1)} hrs`
                }
              ]}
            />
            <GroupedBars
              labels={series.map((s) => s.month)}
              groupA={{ values: opened, label: 'Opened', tone: 'alert' }}
              groupB={{ values: resolved, label: 'Resolved', tone: 'safe' }}
              max={max}
            />
            {(query.data?.top_reasons.length ?? 0) > 0 ? (
              <div>
                <div className="mb-vt-2 text-vt-12 font-semibold uppercase tracking-wide text-vt-text-muted">
                  Top reasons
                </div>
                <ul className="grid grid-cols-1 gap-vt-1 md:grid-cols-2 lg:grid-cols-3">
                  {(query.data?.top_reasons ?? []).slice(0, 5).map((r) => (
                    <li
                      key={r.reason}
                      className="flex items-center justify-between rounded-vt-sm bg-vt-surface-2 px-vt-3 py-vt-2 text-vt-13"
                    >
                      <span className="truncate text-vt-text">{r.reason}</span>
                      <Badge variant="muted">{r.count}</Badge>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ─── C. EV revenue ────────────────────────────────────────

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];

function EvRevenueSection({
  year,
  month,
  onYearChange,
  onMonthChange
}: {
  year: number;
  month: number;
  onYearChange: (y: number) => void;
  onMonthChange: (m: number) => void;
}): JSX.Element {
  const query = useQuery({
    queryKey: ['parking', 'analytics', 'ev-revenue', { year, month }],
    queryFn: () => {
      const qs = new URLSearchParams({
        period_year: String(year),
        period_month: String(month)
      });
      return get<EvRevenueResponse>(`/parking/analytics/ev-revenue?${qs.toString()}`);
    }
  });

  const byCharger = query.data?.by_charger ?? [];
  const chargerValues = byCharger.map((c) => c.amount_inr);
  const max = Math.max(1, ...chargerValues);

  const yearOptions = useMemo(() => {
    const current = new Date().getFullYear();
    return [current - 1, current, current + 1];
  }, []);

  return (
    <Card>
      <CardContent className="space-y-vt-3 p-vt-4">
        <div className="flex flex-wrap items-center justify-between gap-vt-2">
          <SectionHeader icon={<Zap className="h-4 w-4" />} title="C. EV revenue" />
          <div className="flex items-center gap-vt-2">
            <Select
              aria-label="Month"
              value={String(month)}
              onChange={(e) => onMonthChange(Number(e.target.value))}
              className="w-32"
            >
              {MONTHS.map((m, i) => (
                <option key={m} value={i + 1}>
                  {m}
                </option>
              ))}
            </Select>
            <Select
              aria-label="Year"
              value={String(year)}
              onChange={(e) => onYearChange(Number(e.target.value))}
              className="w-24"
            >
              {yearOptions.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </Select>
          </div>
        </div>

        {query.isLoading ? (
          <Skeleton className="h-40 w-full" />
        ) : query.isError ? (
          <p className="text-vt-alert">{getErrorMessage(query.error)}</p>
        ) : !query.data ? (
          <p className="py-vt-4 text-center text-vt-13 text-vt-text-muted">No EV revenue data.</p>
        ) : (
          <>
            <KpiRow
              items={[
                { label: 'Total kWh', value: query.data.total_kwh.toFixed(1) },
                { label: 'Total revenue', value: formatInr(query.data.total_amount_inr) },
                { label: 'Sessions', value: String(query.data.session_count) },
                {
                  label: 'Avg ₹/session',
                  value:
                    query.data.session_count > 0
                      ? formatInr(query.data.total_amount_inr / query.data.session_count)
                      : '—'
                }
              ]}
            />
            {byCharger.length > 0 ? (
              <BarChart
                values={chargerValues}
                max={max}
                labels={byCharger.map((c) => c.label ?? c.charger_id.slice(0, 6))}
                metricLabel="₹ by charger"
                tone="info"
              />
            ) : (
              <p className="text-vt-13 text-vt-text-muted">No charger-level activity this period.</p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ─── D. Marketplace ───────────────────────────────────────

function MarketplaceSection({ from, to }: { from: string; to: string }): JSX.Element {
  const query = useQuery({
    queryKey: ['parking', 'analytics', 'marketplace', { from, to }],
    queryFn: () => {
      const qs = new URLSearchParams({ from, to });
      return get<MarketplaceResponse>(`/parking/analytics/marketplace?${qs.toString()}`);
    }
  });

  return (
    <Card>
      <CardContent className="space-y-vt-3 p-vt-4">
        <SectionHeader icon={<Store className="h-4 w-4" />} title="D. Marketplace" />
        {query.isLoading ? (
          <Skeleton className="h-40 w-full" />
        ) : query.isError ? (
          <p className="text-vt-alert">{getErrorMessage(query.error)}</p>
        ) : !query.data ? (
          <p className="py-vt-4 text-center text-vt-13 text-vt-text-muted">No marketplace data.</p>
        ) : (
          <>
            <KpiRow
              items={[
                { label: 'Active listings', value: String(query.data.active_listings) },
                { label: 'Completed rentals', value: String(query.data.completed_rentals) },
                { label: 'Society cut total', value: formatInr(query.data.society_cut_total_inr) }
              ]}
            />
            <div>
              <div className="mb-vt-2 text-vt-12 font-semibold uppercase tracking-wide text-vt-text-muted">
                Recent transactions
              </div>
              {query.data.recent_transactions.length === 0 ? (
                <p className="text-vt-13 text-vt-text-muted">No recent transactions.</p>
              ) : (
                <ul className="divide-y divide-vt-border rounded-vt-sm border border-vt-border">
                  {query.data.recent_transactions.slice(0, 8).map((t) => (
                    <li
                      key={t.id}
                      className="flex items-center justify-between gap-vt-3 px-vt-3 py-vt-2 text-vt-13"
                    >
                      <div className="min-w-0">
                        <div className="truncate font-medium text-vt-text">Slot {t.slot_number}</div>
                        <div className="text-vt-12 text-vt-text-muted">
                          {t.from_unit ?? '—'} → {t.to_unit ?? '—'} · {formatDate(t.completed_at)}
                        </div>
                      </div>
                      <div className="font-mono text-vt-text">{formatInr(t.amount_inr)}</div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ─── E. Utilization ───────────────────────────────────────

function UtilizationSection({ from, to }: { from: string; to: string }): JSX.Element {
  const utilQuery = useQuery({
    queryKey: ['parking', 'analytics', 'utilization', { from, to }],
    queryFn: () => {
      const qs = new URLSearchParams({ days: '90' });
      return get<UtilizationResponse>(`/parking/analytics/utilization?${qs.toString()}`);
    }
  });

  const underusedQuery = useQuery({
    queryKey: ['parking', 'analytics', 'underused-slots'],
    queryFn: () => get<UnderusedResponse>(`/parking/analytics/underused-slots?days=30`)
  });

  const byZone = utilQuery.data?.by_zone ?? [];
  const zoneValues = byZone.map((z) => z.occupied_pct);
  const zoneMax = Math.max(100, ...zoneValues, 1);

  return (
    <Card>
      <CardContent className="space-y-vt-3 p-vt-4">
        <SectionHeader icon={<Activity className="h-4 w-4" />} title="E. Utilization" />

        {utilQuery.isLoading ? (
          <Skeleton className="h-40 w-full" />
        ) : utilQuery.isError ? (
          <p className="text-vt-alert">{getErrorMessage(utilQuery.error)}</p>
        ) : byZone.length === 0 ? (
          <p className="text-vt-13 text-vt-text-muted">No zone data.</p>
        ) : (
          <div>
            <div className="mb-vt-2 text-vt-12 font-semibold uppercase tracking-wide text-vt-text-muted">
              By zone (% occupied)
            </div>
            <BarChart
              values={zoneValues}
              max={zoneMax}
              labels={byZone.map((z) => z.zone_name)}
              metricLabel="% occupied"
              tone="info"
            />
          </div>
        )}

        <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-2">
          {/* Top underused */}
          <div>
            <div className="mb-vt-2 text-vt-12 font-semibold uppercase tracking-wide text-vt-text-muted">
              Top 10 underused slots
            </div>
            {underusedQuery.isLoading ? (
              <Skeleton className="h-24 w-full" />
            ) : (underusedQuery.data?.items.length ?? 0) === 0 ? (
              <p className="text-vt-13 text-vt-text-muted">No underused slots found.</p>
            ) : (
              <ul className="divide-y divide-vt-border rounded-vt-sm border border-vt-border">
                {(underusedQuery.data?.items ?? []).slice(0, 10).map((s) => (
                  <li
                    key={s.slot_id}
                    className="flex items-center justify-between gap-vt-2 px-vt-3 py-vt-2 text-vt-13"
                  >
                    <div className="min-w-0">
                      <div className="truncate font-medium text-vt-text">
                        Slot {s.slot_number}
                        {s.zone_name ? (
                          <span className="ml-1 text-vt-text-muted">· {s.zone_name}</span>
                        ) : null}
                      </div>
                      <div className="text-vt-12 text-vt-text-muted">
                        {s.owner_unit ? `Owner ${s.owner_unit} · ` : ''}
                        {s.entries_30d} entries in 30d
                      </div>
                    </div>
                    <Badge variant="warn">Convert?</Badge>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Top disputed */}
          <div>
            <div className="mb-vt-2 text-vt-12 font-semibold uppercase tracking-wide text-vt-text-muted">
              Top 10 most-disputed slots
            </div>
            {utilQuery.isLoading ? (
              <Skeleton className="h-24 w-full" />
            ) : (utilQuery.data?.top_disputed.length ?? 0) === 0 ? (
              <p className="text-vt-13 text-vt-text-muted">No dispute hotspots.</p>
            ) : (
              <ul className="divide-y divide-vt-border rounded-vt-sm border border-vt-border">
                {(utilQuery.data?.top_disputed ?? []).slice(0, 10).map((s) => (
                  <li
                    key={s.slot_id}
                    className="flex items-center justify-between gap-vt-2 px-vt-3 py-vt-2 text-vt-13"
                  >
                    <div className="min-w-0">
                      <div className="truncate font-medium text-vt-text">
                        Slot {s.slot_number}
                        {s.zone_name ? (
                          <span className="ml-1 text-vt-text-muted">· {s.zone_name}</span>
                        ) : null}
                      </div>
                      <div className="text-vt-12 text-vt-text-muted">Clarify rules</div>
                    </div>
                    <Badge variant="alert">{s.dispute_count}</Badge>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── primitives ───────────────────────────────────────────

function SectionHeader({ icon, title }: { icon: React.ReactNode; title: string }): JSX.Element {
  return (
    <div className="flex items-center gap-vt-2">
      <span className="text-vt-text-muted">{icon}</span>
      <h2 className="text-vt-14 font-semibold text-vt-text">{title}</h2>
    </div>
  );
}

function KpiRow({ items }: { items: Array<{ label: string; value: string }> }): JSX.Element {
  return (
    <div className="grid grid-cols-2 gap-vt-3 sm:grid-cols-3 lg:grid-cols-4">
      {items.map((it) => (
        <div key={it.label} className="rounded-vt-sm bg-vt-surface-2 px-vt-3 py-vt-2">
          <div className="text-vt-12 text-vt-text-muted">{it.label}</div>
          <div className="mt-1 font-mono text-vt-18 font-semibold text-vt-text">{it.value}</div>
        </div>
      ))}
    </div>
  );
}

type Tone = 'primary' | 'info' | 'safe' | 'alert' | 'warn';
const TONE_FILL: Record<Tone, string> = {
  primary: 'fill-vt-primary',
  info: 'fill-vt-info',
  safe: 'fill-vt-safe',
  alert: 'fill-vt-alert',
  warn: 'fill-vt-warn'
};

function BarChart({
  values,
  max,
  labels,
  metricLabel,
  tone = 'primary'
}: {
  values: number[];
  max: number;
  labels: string[];
  metricLabel: string;
  tone?: Tone;
}): JSX.Element {
  const W = 600;
  const H = 160;
  const PAD_TOP = 8;
  const PAD_BOTTOM = 22;
  const PAD_LEFT = 4;
  const PAD_RIGHT = 4;
  const chartW = W - PAD_LEFT - PAD_RIGHT;
  const chartH = H - PAD_TOP - PAD_BOTTOM;
  const n = values.length;
  const barSlot = chartW / Math.max(n, 1);
  const barW = Math.max(1, barSlot - 2);

  const tickIndices = useMemo(() => {
    if (n === 0) return [] as number[];
    const target = Math.min(6, n);
    if (target === 1) return [0];
    const step = (n - 1) / (target - 1);
    const ticks = new Set<number>();
    for (let i = 0; i < target; i += 1) ticks.add(Math.round(i * step));
    return Array.from(ticks).sort((a, b) => a - b);
  }, [n]);

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      role="img"
      aria-label={`${metricLabel} across ${n} buckets`}
      className="h-40 w-full"
    >
      <line
        x1={PAD_LEFT}
        y1={H - PAD_BOTTOM}
        x2={W - PAD_RIGHT}
        y2={H - PAD_BOTTOM}
        stroke="currentColor"
        strokeOpacity="0.18"
        strokeWidth="1"
      />
      {values.map((v, i) => {
        const h = (v / max) * chartH;
        const x = PAD_LEFT + i * barSlot + (barSlot - barW) / 2;
        const y = H - PAD_BOTTOM - h;
        return (
          <rect
            key={i}
            x={x}
            y={y}
            width={barW}
            height={Math.max(h, v === 0 ? 0 : 1)}
            rx={barW > 4 ? 2 : 0}
            className={TONE_FILL[tone]}
            tabIndex={0}
            focusable="true"
          >
            <title>{`${labels[i] ?? ''} — ${v.toLocaleString('en-IN', {
              maximumFractionDigits: 1
            })}`}</title>
          </rect>
        );
      })}
      {tickIndices.map((i) => {
        const x = PAD_LEFT + i * barSlot + barSlot / 2;
        const label = labels[i] ?? '';
        const short = label.length > 10 ? `${label.slice(0, 10)}…` : label;
        return (
          <text
            key={`tick-${i}`}
            x={x}
            y={H - 6}
            textAnchor="middle"
            className="fill-current text-vt-text-soft"
            style={{ fontSize: '10px' }}
          >
            {short}
          </text>
        );
      })}
    </svg>
  );
}

function GroupedBars({
  labels,
  groupA,
  groupB,
  max
}: {
  labels: string[];
  groupA: { values: number[]; label: string; tone: Tone };
  groupB: { values: number[]; label: string; tone: Tone };
  max: number;
}): JSX.Element {
  const W = 600;
  const H = 180;
  const PAD_TOP = 8;
  const PAD_BOTTOM = 36;
  const PAD_LEFT = 4;
  const PAD_RIGHT = 4;
  const chartW = W - PAD_LEFT - PAD_RIGHT;
  const chartH = H - PAD_TOP - PAD_BOTTOM;
  const n = labels.length;
  const groupSlot = chartW / Math.max(n, 1);
  const barW = Math.max(1, (groupSlot - 4) / 2);

  return (
    <div>
      <div className="mb-vt-2 flex items-center gap-vt-3 text-vt-12 text-vt-text-muted">
        <span className="inline-flex items-center gap-1">
          <span className={`inline-block h-2 w-2 rounded-sm ${TONE_FILL[groupA.tone]}`} />
          {groupA.label}
        </span>
        <span className="inline-flex items-center gap-1">
          <span className={`inline-block h-2 w-2 rounded-sm ${TONE_FILL[groupB.tone]}`} />
          {groupB.label}
        </span>
      </div>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        role="img"
        aria-label={`${groupA.label} vs ${groupB.label}`}
        className="h-44 w-full"
      >
        <line
          x1={PAD_LEFT}
          y1={H - PAD_BOTTOM}
          x2={W - PAD_RIGHT}
          y2={H - PAD_BOTTOM}
          stroke="currentColor"
          strokeOpacity="0.18"
          strokeWidth="1"
        />
        {labels.map((label, i) => {
          const ax = PAD_LEFT + i * groupSlot + 2;
          const bx = ax + barW + 2;
          const av = groupA.values[i] ?? 0;
          const bv = groupB.values[i] ?? 0;
          const ah = (av / max) * chartH;
          const bh = (bv / max) * chartH;
          return (
            <g key={i}>
              <rect
                x={ax}
                y={H - PAD_BOTTOM - ah}
                width={barW}
                height={Math.max(ah, av === 0 ? 0 : 1)}
                rx={2}
                className={TONE_FILL[groupA.tone]}
              >
                <title>{`${label} · ${groupA.label}: ${av}`}</title>
              </rect>
              <rect
                x={bx}
                y={H - PAD_BOTTOM - bh}
                width={barW}
                height={Math.max(bh, bv === 0 ? 0 : 1)}
                rx={2}
                className={TONE_FILL[groupB.tone]}
              >
                <title>{`${label} · ${groupB.label}: ${bv}`}</title>
              </rect>
              <text
                x={ax + barW + 1}
                y={H - 20}
                textAnchor="middle"
                className="fill-current text-vt-text-soft"
                style={{ fontSize: '10px' }}
              >
                {label.length > 10 ? `${label.slice(0, 10)}…` : label}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
